import Map from "ol/Map";
import View from "ol/View";
import Overlay from "ol/Overlay";
import LayerBase from "ol/layer/Base";
import LayerVector from "ol/layer/Vector";
import LayerTile from "ol/layer/Tile";
import SourceVector from "ol/source/Vector";

import Collection from "ol/Collection";
import Control from "ol/control/Control";
import Interaction from "ol/interaction/Interaction";
import Select from "ol/interaction/Select";
import Feature from "ol/Feature";
import RenderFeature from "ol/render/Feature";
import Style from "ol/style/Style";
import OverlayPositioning from "ol/OverlayPositioning";

import SimpleGeometry from "ol/geom/SimpleGeometry";
import GeometryType from "ol/geom/GeometryType";
import MapBrowserEvent from "ol/MapBrowserEvent";

import GoogleLayer from 'olgm/layer/Google.js';
import GoogleMap from "./GoogleMap";

import { IAdminArea, ITheme } from "../shared/types";

export type Coordinate = number[];
export type Extent = [number, number, number, number];
export type TileCoord = [number, number, number];
export type RendererType = "canvas" | "dom" | "webgl";
export type EventsConditionType = (event: MapBrowserEvent) => boolean;
export type StyleFunction = (feature: (Feature | RenderFeature), resolution: number) => (Style | Style[]);
export type DrawGeometryFunctionType = (coords: (Coordinate | Coordinate[] | Coordinate[][]), geo?: SimpleGeometry) => SimpleGeometry;
export type PopupInfoType = "text" | "photo" | "photos" | "list" | "props" | "table" | "form";
export type FeatureLayerName = "DrawingLayer" | "SelectedFeatureLayer" | 
                               "AoiLayer" | "SearchFeatureLayer";
export enum EGoogleMapType {
  MAP_TYPE_NORMAL = 1,    //kGMSTypeNormal
  MAP_TYPE_SATELLITE,     //kGMSTypeSatellite
  MAP_TYPE_TERRAIN,       //kGMSTypeTerrain
  MAP_TYPE_HYBRID,        //kGMSTypeHybrid
  MAP_TYPE_NONE           //kGMSTypeNone
};

export interface IFormatOptions {
  dataProjection?: string;
  featureProjection?: string;
}

export interface IGeoJSON {
  featureClass?: string;
  type?: string;
  properties: {[key:string] : any};
  geometry: {
    type: string;
    coordinates: any;
  },
  featureOnMap?: Feature;
  features?: IGeoJSON[]; //reserved
}

export interface ILayerForEdit {
  Point?: LayerVector[];
  Line?: LayerVector[];
  Polygon?: LayerVector[];
};

export interface IPoint {
  Longitude: number;
  Latitude: number;
};

export interface IBaseMapSource {
  Type: string;
  Id: string;
  IsImage?: boolean;
  wmtsMatrixSet?: string;
  featureType?: string;
  Culture?: string;
  Url?: string;
  StaticMapUrl?: string;
  Zoom?: number;
  Sizes?: [number][];
  Extent?: [number, number, number, number];
  Resolutions?: number[];
  Origin?: [number, number];
  Origins?: [number, number][];
  MinZoom?: number;
  Layer?: LayerVector | LayerTile | GoogleLayer;
  Legend?: string;
  LegendLayer?: LayerVector | LayerTile | GoogleMap;
  NativeMap?: Map;
  Attributions?: string[];

  //FOR TEST using internal WMS as Basemap
  service?: string;
  url?: string;
  params?: any;
}

export interface IMapLayerSource {
  id?: string;
  service?: 'WMTS' | 'WMS' | 'WFS' | 'Vector';
  useGwc?: boolean;
  serverType?: string; // 'geoserver',
  url?: string;
  extent?: Extent;
  authen?: {
    username: string;
    password: string;
  };
  params?: {[key:string] : any}; // WMS API params
  params2?: {[key:string] : any}; // used to replace params, such as for radio layers
  //layerOptions?: {[key:string] : any}; // used to replace params.LAYERS, such as for radio layers
  wmtsMatrixSet?: string;
  wmtsEPSG?: string;
  filterOptions?: string[]; //{[key:string] : string};
  geoJSONs?: IGeoJSON[]; // holds initialize features for Vector service
  featureType?: string; // 'gisagro:farmland_new',
  geometryType?: 'Polygon' | 'Line' | 'Point',
  load?: Function;
  isMulti?: boolean;
  canEdit?: boolean; // for vector,
  canHover?: boolean;
  canQuery?: boolean;
  canSnap?: boolean;
  strategy?: string; //'', //always use bbox
  crossOrigin?: string;
  transition?: number;
  maxZoomLevel?: number;
  maxEditResolution?: number; // threshhold to trigger loading feature from server
  maxResolution?: number; // threshhold for making layer visible
  minResolution?: number; 
  style?: Function | Style; // for WFS or Vector layer, to create an Object for STYLE.create()
  zIndex?: number;
  zIndexOffset?: number;
  visibilities?:{[key:string] : boolean}; //used to control feature visibility by its attrs
  visible?: boolean; // display it after added to map
  opacity?: number;
  clusterDistance?: number;
  mapLayers?: IMapLayer[];
  nativeLayer?: LayerVector | LayerTile;
  events?: {[key:string] : Function};
}

export interface IMapLayerIcon {
  name: string;
  style: {[key:string] : any};
}

export interface IMapLayer {
  index?: number;
  id?: string;
  uri?: string; // RESTful uses this to get detail such as map source from database
  header?: string | string[];
  title?: string | string[];
  replacer?: any[]; // for filter L(replacer)
  subtitle?: string | string[];
  subtitleReplacer?: any[];
  type?: "checkbox" | "radio"; // if defined, an internal action:'changeChecked' will be toggled
  typeText?: string; 
  radioGroup?: string;
  lastCheckedRadioIndex?: number; // the number value is the index in parent.children
  active?: boolean;
  checked?: boolean | "indeterminate";
  iconChecked?: string;
  iconUnChecked?: string;
  grantToUser?: Function;
  geomType?: "Polygon" | "Line" | "Point" | "Complex";
  reportable?: boolean;
  dashboard?: boolean;
  mapSources?: IMapLayerSource[];
  visible?: boolean;
  setVisibility?: Function; //called instead of layer.setVisible(), which is to control feature visibility by its props
  parent?: IMapLayer;
  dense?: boolean; // make child list height dense
  showDivider?: boolean; // show or hide divider between child list
  children?: IMapLayer[];
  legend?: IMapLayer[]; // these are leaf that won't be shown in the layer store list
  // legend style that can consits of EITHER of style, imgUrl or icons[]
  // we can overlay multiple icons[] to make a composite one
  className?: string;
  style?: {[key:string] : any};
  image?: string;
  icons?: IMapLayerIcon[];
  showCtrls?: boolean; // show the visibility control or not
  actions?: {
    action?: string;
    do?: Function;
    type?: "button" | "select";
    width?: string; // 'auto' | '100px'
    icon?: string;
    iconText?: string;
    selected?: any;
    options?: { // options for select
      title?: string;
      value?: any;
    }[]
  }[];
  doAction?: Function;
  hoverInfo?: Function;
  onVisibilityChange?: Function;
}

export interface IDMS {
  degree: number;
  minute: number;
  second: number;
}

export interface IBaseMapProvider {
  Name: string;
  Key?: string;
  Maps: IBaseMapSource[];
  GMap?: any;
}

export interface ICoordinateReadoutTarget {
  wgs84?: Element;
  utm?: Element;
}

export interface IMapControlTarget {
  rotate?: Element;
  scale?: Element;
  wgs84?: Element;
  utm?: Element;
};

export interface IDefaultControlOptions {
  attribution?: boolean; //Attribution. Default is true.
  attributionOptions?: {[key:string] : any}; //olx.control.AttributionOptions
  rotate?: boolean; //Rotate. Default is true.
  rotateOptions?: {[key:string] : any}; //olx.control.RotateOptions
  zoom?: boolean; //Zoom. Default is true.
  zoomOptions?: {[key:string] : any}; //olx.control.ZoomOptions
}

export interface IDefaultInteractionOptions {
  altShiftDragRotate?: boolean; //Whether Alt-Shift-drag rotate is desired. Default is true.
  constrainResolution?: boolean; //Zoom to the closest integer zoom level after the wheel/trackpad or pinch gesture ends. Default is false.
  doubleClickZoom: boolean; //Whether double click zoom is desired. Default is true.
  keyboard?: boolean; //Whether keyboard interaction is desired. Default is true.
  mouseWheelZoom?: boolean; //Whether mousewheel zoom is desired. Default is true.
  shiftDragZoom?: boolean; //Whether Shift-drag zoom is desired. Default is true.
  dragPan?: boolean; //Whether drag pan is desired. Default is true.
  pinchRotate?: boolean; //Whether pinch rotate is desired. Default is true.
  pinchZoom?: boolean; //Whether pinch zoom is desired. Default is true.
  zoomDelta?: number; //Zoom delta.
  zoomDuration?: number; //Zoom duration.
}
export interface ILogoOptions {
  href: string;
  src: string;
}

export interface IMapOptions {
  controls?: (Collection<Control> | Control[]);
  pixelRatio?: number;
  interactions?: (Collection<Interaction> | Interaction[]);
  keyboardEventTarget?: (HTMLElement | Document | string);
  layers?: (LayerBase[] | Collection<LayerBase>);
  loadTilesWhileAnimating?: boolean;
  loadTilesWhileInteracting?: boolean;
  logo?: (boolean | string | ILogoOptions | HTMLElement);
  moveTolerance?: number;
  overlays?: (Collection<Overlay> | Overlay[]);
  renderer?: (RendererType | Array<(RendererType | string)> | string);
  target?: (HTMLElement | string);
  view?: View;
}

export interface IDrawOptions {
  clickTolerance?: number;
  features?: Collection<Feature>;
  source?: SourceVector;
  snapTolerance?: number;
  type: GeometryType;
  maxPoints?: number;
  minPoints?: number;
  finishCondition?: EventsConditionType;
  style?: (Style | Style[] | StyleFunction);
  geometryFunction?: DrawGeometryFunctionType;
  geometryName?: string;
  condition?: EventsConditionType;
  freehandCondition?: EventsConditionType;
  freehand?: boolean;
  wrapX?: boolean;
}

export interface IMapEvent {
  showFeatureInfo?: any;
}

export interface ICreateMapOptions {
  mapOptions?: IMapOptions;
  baseLayer?: Array<number>; //[IndexOfBASEMAP,IndexOfSourceOfSelectedBASEMAP]
  defaultInteractions?: IDefaultInteractionOptions;
  defaultControls?: IDefaultControlOptions;
  interactions?: Array<'DragFeature'>;
  controlTargets?: IMapControlTarget;
  events?: IMapEvent;
};

export interface IOverlay {
  position?: Coordinate;
  id?: string;
  element?: HTMLElement;
  infoType?: PopupInfoType;
  bubbling?: boolean;
  positioning?: OverlayPositioning; //"bottom-left" | "bottom-center" | "bottom-right" | "center-left" | "center-center" | "center-right" | "top-left" | "top-center" | "top-right";
  offset?: number[];
  className?: string;
  style?: string;
  okText?: string;
  methods?: Object;
  cancelText?: string;
  autoPan?: boolean;
  zoomTo?: boolean;
  onClose?: Function;
  state?: {[key:string] : any};
};


export interface IGPS {
  /**
   * The latitude of the geolocation, in degrees.
   */
  Latitude?: number;

  /**
   * The longitude of the geolocation, in degrees.
   */
  Longitude?: number;

  /**
   * The altitude (if available), in meters above sea level.
   */
  Altitude?: number;

  /**
   * The horizontal accuracy, in meters.
   */
  HorizontalAccuracy?: number;

  /**
   * The vertical accuracy, in meters.
   */
  VerticalAccuracy?: number;

  /**
   * The speed, in meters/second over ground.
   */
  Speed?: number;

  /**
   * The direction (course), in degrees.
   */
  Direction?: number;

  /**
   * The time at which this location was determined.
   */
  Timestamp?: Date;
};

/**
 * ILocation
 * represent a geographic location that can be shown on map
 */
export interface ILocation {
  GPS?: IGPS;
  Longitude?: number;
  Latitude?: number;
  Address?: string;
  Vicinity?: string;
  Area?: IAdminArea;
  Watcher?: any;
}

/**
 * IAddress
 * the representation of tradictional mailling address
 */
export interface IAddress {
  AddressNo?: string;
  Moo?: string;
  Alley?: string;
  Street?: string;
  Village?: string;
  Town?: string;
  County?: string;
  Province?: string;
  Country?: string;
  ZipCode?: string;
}

export interface IAoi {
  code?: number;
  name?: string;
  /*logo?: string;
  banner?: string;
  splash?: string;
  login?: string;
  colors?: {[key:string] : any};*/
  theme?: ITheme;
  extent?: [number,number,number,number];
  feature?: Feature;
}

export interface IMeasurer {
  name: "distance" | "area2" | "coordinate";
  icon?: "straighten" | "flip_to_front" | "location_on";
  unit: "metric" | "thai";
  crs: "WGS84" | "UTM",
  target?: Element
};

export interface IFlashPin {
  id: string;
  pin: Feature; //Point Feature
  start: number;
  radius?: number;
  fillColor?: [number,number,number,number]; // [R,G,B,A]
  strokeColor?: [number,number,number,number];
}

export interface IMapAction {
  name?: string;
  title?: string;
  layerId?: string;
  params?: any;
  tool?: any; // ol.Control | ol.Interaction
  lastTool?: any;
  isEditingTool?: boolean;
}

export interface IMap {
  Id?: string;
  Action?: IMapAction;
  BaseMaps?: IBaseMapProvider[],
  //BaseMapId?: string; 
  BaseMap?: LayerTile | GoogleLayer;
  BaseMapType?: "image" | "map"; // for control basemap list text style
  HasGoogleMap?: boolean; // GISTDA MWA Project requires no Google Map
  NativeMap?: Map;
  GoogleMap?: any; //google.maps.Map()
  MapLayers?: IMapLayer[]; // all layers that are shown on map, such as those from ./data/layers.ts
  TileLayers?: LayerTile[];
  VectorLayers?: LayerVector[];
  AoiLayer?: LayerVector;
  DrawingLayer?: LayerVector;
  SearchFeatureLayer?: LayerVector;
  SelectedFeatureLayer?: LayerVector;
  HighlightedFeature?: Feature;
  HighlightFeatureTool?: Select;
  Filter?: {[key:string] : string};
  FlashPins?: IFlashPin[];
  InfoPopup?: Overlay;
  HoverInfo?: Overlay;
  XDPI?: number;
  YDPI?: number;
}

export interface IMapCoordinate {
  pixel?: Coordinate;
  map?: Coordinate;
  wgs84?: Coordinate;
  utm?: {
    x?: number;
    y?: number;
    zone?: number;
    NS?: "N" | "S";
  };
}

export interface IMapViewProps {
  extent: [number,number,number,number];
  padding: number[];
  center: number[];
  zoom: number;
}

export interface IMapViewState {
  layers?: IMapLayer[];
  viewState?: IMapViewProps;
  aoi?: {
    code?: number;
    extent?: [number,number,number,number],
    feature?: Feature;
  }
}
export interface ICodeList {
  code: string | number;
  name: string;
  abbr?: string;
  phone?: string;
  name_en?: string;
  name_th?: string;
  name_zh?: string;
  bbox?: Extent;
  feature?: any;
  zip_code?: string | number;
  disabled?: boolean;
}

export interface IThaiArea {
  Rai?: number;
  Ngan?: number;
  SqWah?: number;
}

export interface IMapFilter {
  Source?: string; //"GISagro" | "DOL" | "PLACE";
  Keyword?: string;
  ProvinceCode?: number;
  AmphoeCode?: number;
  TambonCode?: number;
  AreaText?: string;
  CropType?: number;
  CropGenus?: number;
  CropSpecies?: number;
  CropText?: string;
  DeedType?: string;
  GrowOrHarvest?: "grow" | "harvest";
  GrowOrHarvestYear?: number;
  AreaType?: "lte" | "gte" | "between";
  Area1?: IThaiArea;
  Area2?: IThaiArea;
}

export interface IMapViewExtent {
  map: Extent;
  wgs84: Extent;
  utm: Extent;
}

export interface IMapViewInfo {
  size?: number[];
  zoom?: number;
  rotation?: number;
  center?: IMapCoordinate;
  paddingCenter?: IMapCoordinate;
  extent?: IMapViewExtent;
}

export interface ITool {
  name?: string;
  title?: string;
  icon?: string
  isEnabled?: boolean;
  do?: Function;
}

export interface IUndoRedo {
  undo: ITool;
  redo: ITool;
  save: ITool;
}

export interface IMeasureResult {
  value: number | number[];
  unit?: string;
  content?: string;
}

export interface IHoverInfo {
  title?: string;
  items?: string[];
}

export interface IPopUpInfo {
  infoid?: string; // for internal use
  info?: string;
  action?: Function;
  actionIcon?: string;
  data?: {[key:string] : any};
}

export interface IFeatureInfo {
  isCluster?: boolean;
  feature?: Feature;
  features?: Feature[]; // for features Cluster
  location?: IMapCoordinate;
  fetching?: boolean;
}

export interface IAutoSearch {
  url: string
  data?: string;
  PHOTO?: string;
}

export interface IInfoProperty {
  itemIndex?: number;
  propertyIndex?: number;
  isKey?: boolean;
  name: string;
  alias?: string;
  source?: string | string[] | {[key:string]:string};
  originalValue?: string | number | Date;
  value?: any;
  getValue?: Function; //get value of a property item
  mandatory?: boolean;
  required?: boolean; //conditionally mandatory
  canEdit?: boolean;
  ctrlType?: "property" | "select" | "list" | "photo" | "radio" | "checkbox" | "date" | "textarea" | "hidden";
  dataType?: "number" | "decimal" | "integer" | "text" | "digits" | "boolean" | "date" | "object",
  placeholder?: string;
  options?: ICodeList[];
  user?: any; // User;
  setProperty?: Function;
  loadOptions?: Function;
  onOptionChange?: Function;
  fetchOptions?: Function;
  upload?: string;
  photoUrl?: Function;
  searching?: boolean;
  validate?: Function;
  autoSearch?: Function; //IAutoSearch;
  featureOnMap?: Feature;
  mask?: string; // i.e. "0-##-###-####"
}

export interface IEditingFeature {
  dirty?: Feature[];  // deleted newly inserted features
  insert?: Feature[]; //IGeoJSON[];
  update?: Feature[]; //IGeoJSON[];
  delete?: Feature[]; //IGeoJSON[];
}

export interface IFeatureToSave {
  total: number;
  points?: IEditingFeature,
  lines?: IEditingFeature,
  polygons?: IEditingFeature
}

export interface IFeatureToSaveByIntersects {
  intersected?: IFeatureToSave;
  notIntersected?: IFeatureToSave;
}

export interface IInfoSchemaItem {
  group?: string;
  properties: IInfoProperty[];
}

export interface ISourceKey {
  name: string;
  primary: string;
  foreigns?: {[key:string] : string}; // such as farming.gid_fk -> farmland.gid
}

export interface IInfoSchema {
  title?: string;
  saveOrder?: ISourceKey[]; 
  items?: IInfoSchemaItem[];
  canEdit?: boolean;
  feature?: Feature;
}

export interface IModifiedFeature {
  original?: Feature;
  modified?: Feature;
}

export interface ICutFeature extends IModifiedFeature {
}

export interface IMovedFeature extends IModifiedFeature {
}

export interface ISplitFeature extends IModifiedFeature {
}

export interface ISplit {
  splits?: ISplitFeature[];   // all splits by a LineString, or those outside of a Polygon splitter
  splits2?: ISplitFeature[];  // null if splitted by a LineString, or those inside a Polygon splitter
}

export interface ISplitResult extends ISplit {
  splitter: Feature;    // a LineString or Polygon to split the target
  targets: Feature[];
}
